import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Search } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

const STATUSES = { received: "Rebuda", reviewing: "En revisió", in_progress: "En procés", resolved: "Resolta" };
const CATS = { cleaning: "Neteja", urban_furniture: "Mobiliari", lighting: "Il·luminació", signage: "Senyalització", security: "Seguretat", other: "Altres" };

export default function AdminIncidents() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (category !== "all") params.category = category;
    const { data } = await api.get("/incidents", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, category]);

  const changeStatus = async (id, s) => {
    await api.put(`/incidents/${id}/status`, { status: s });
    toast.success("Estat actualitzat"); load();
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("ss_token");
    const res = await fetch(`${API_BASE}/exports/incidents.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "incidencies.csv"; a.click();
  };

  const withCoords = items.filter((i) => i.lat && i.lng);
  const center = withCoords.length > 0 ? [withCoords[0].lat, withCoords[0].lng] : [41.0764, 1.1414];

  return (
    <div className="space-y-6" data-testid="admin-incidents">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-accent">Via pública</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Incidències</h1>
        </div>
        <Button variant="outline" onClick={exportCsv} className="rounded-full" data-testid="incidents-export-btn"><Download className="w-4 h-4 mr-2" /> CSV</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca descripció" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full w-64" data-testid="incidents-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-full w-44" data-testid="incidents-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots els estats</SelectItem>
            {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="rounded-full w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Totes les categories</SelectItem>
            {Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="rounded-full">
          <TabsTrigger value="list" className="rounded-full" data-testid="incidents-tab-list">Llistat</TabsTrigger>
          <TabsTrigger value="map" className="rounded-full" data-testid="incidents-tab-map">Mapa</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="space-y-3">
          {items.map((i) => (
            <Card key={i.id} className="rounded-2xl border-2" data-testid={`incident-row-${i.id}`}>
              <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                {i.photo_url && <img src={i.photo_url} alt="" className="w-full md:w-24 h-24 rounded-xl object-cover" />}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-accent font-semibold">{CATS[i.category] || i.category}</div>
                  <div className="font-medium mt-1">{i.description}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {i.name || "Anònim"} · {i.address || (i.lat ? `${i.lat.toFixed(4)}, ${i.lng.toFixed(4)}` : "sense ubicació")} · {new Date(i.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Select value={i.status} onValueChange={(v) => changeStatus(i.id, v)}>
                  <SelectTrigger className="rounded-full w-40" data-testid={`incident-status-${i.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <div className="text-muted-foreground text-sm p-8 text-center">Sense incidències.</div>}
        </TabsContent>
        <TabsContent value="map">
          <Card className="rounded-2xl border-2 overflow-hidden">
            <div style={{ height: "500px", width: "100%" }} data-testid="incidents-map">
              <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
                {withCoords.map((i) => (
                  <Marker key={i.id} position={[i.lat, i.lng]}>
                    <Popup>
                      <div className="text-xs uppercase font-semibold">{CATS[i.category]}</div>
                      <div className="text-sm">{i.description}</div>
                      <div className="text-xs mt-1">Estat: <strong>{STATUSES[i.status]}</strong></div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
